import { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Collapsible,
  Data,
  DataFilter,
  DataFilters,
  Grid,
  ResponsiveContext,
  TextInput, 
} from 'grommet';

import { Down, Next } from 'grommet-icons';
import { ListResults } from '../ListResults';

const allReferences = require('../../../data/hoh/hoh-references.json');

const pageContentGrid = {
  columns: {
    xsmall: 'auto',
    small: 'auto',
    medium: [['small', '1/3'], 'flex'],
    large: [['20%', '25%'], 'flex'],
    xlarge: [['1/4', 'medium'], 'flex'],
  },
  gap: {
    xsmall: 'medium',
    small: 'medium',
    medium: 'medium',
    large: 'large',
    xlarge: 'large',
  },
};

export const ReferencesComp = () => {
  return (
    <Box pad={{ top: 'medium' }}>
      <ContentLayout />
    </Box>
  );
};

const properties = {
  workloads: {
    label: 'Workloads',
    options: [
      { label: 'AI', value: 'AI' },
      { label: 'Machine Learning', value: 'Machine learning' },
      { label: 'Data Analytics', value: 'Data analytics' },
      { label: 'SAP', value: 'SAP' },
      { label: 'Observability', value: 'Observability' },
      { label: 'Developer Tools', value: 'Developer tools' },
      { label: 'Database', value: 'Database' },
      { label: 'Data Protection', value: 'Data Protection' },
    ],
  },
  sustainability: { label: 'Sustainability' },
  security: { label: 'Security' },
  tech: { label: 'Technology',},
  delivery: { label: 'Delivery Methods' },
  pricing: { label: 'Business Models' },
};

const Filter = ({ label, expand, setExpand, property }) => {
  return (
    <>
      <Button
        label={label}
        icon={expand ? <Down /> : <Next />}
        onClick={() => setExpand(!expand)}
      />
      <Collapsible open={expand}>
        <Box pad={{ left: 'medium', top: 'xsmall' }}>
          <DataFilter property={property} />
        </Box>
      </Collapsible>
    </>
  );
};

Filter.propTypes = {
  label: PropTypes.string.isRequired,
  expand: PropTypes.bool.isRequired,
  setExpand: PropTypes.func.isRequired,
  property: PropTypes.string.isRequired,
};

const ContentLayout = () => {
  const breakpoint = useContext(ResponsiveContext);
  const showFiltersButton = ['xsmall', 'small'].includes(breakpoint);

  const [expandCategory, setExpandCategory] = useState(false);
  const [expandTechnology, setExpandTechnology] = useState(false);
  const [expandPricing, setExpandPricing] = useState(false);
  const [expandSustainability, setExpandSustainability] = useState(false);
  const [expandSecurity, setExpandSecurity] = useState(false); 
  
  // New state for the search input
  const [searchQuery, setSearchQuery] = useState('');

  // Function to filter references based on search query
  const filteredReferences = allReferences.filter(reference => {
    const query = searchQuery.toLowerCase();
    return (
      (reference.title?.toLowerCase().includes(query) ?? false) ||
      (reference.tech?.toLowerCase().includes(query) ?? false) ||
      (reference.security?.toLowerCase().includes(query) ?? false) ||
      (reference.sustainability?.toLowerCase().includes(query) ?? false) ||
      (reference.publisher?.toLowerCase().includes(query) ?? false) ||
      (reference.pricing?.toLowerCase().includes(query) ?? false) ||
      (reference.delivery?.toLowerCase().includes(query) ?? false) ||
      (reference.workloads?.some(w => w.toLowerCase().includes(query)) ?? false)
    );
  });

  const sidebar = showFiltersButton ? (
    <DataFilters layer />
  ) : (
    <Box
      direction="column"
      align="start"
      gap="small"
      background="background-front"
      elevation="medium"
      pad={{ right: 'small', vertical: 'small' }}
      round="xsmall"
    >
      {/* Add the TextInput here for the search filter */}
      <Box pad={{ left: 'small' }} width="medium">
        <TextInput
          placeholder="Search references..."
          value={searchQuery}
          onChange={event => setSearchQuery(event.target.value)} // Update the search query state
        />
      </Box>

      <Filter
        label="Workloads"
        expand={expandCategory}
        setExpand={setExpandCategory}
        property="workloads"
      />
      <Filter
        label="Technology"
        expand={expandTechnology}
        setExpand={setExpandTechnology}
        property="tech"
      />

      <Filter
        label="Sustainability"
        expand={expandSustainability}
        setExpand={setExpandSustainability}
        property="sustainability"
      /> 
      <Filter
        label="Security"
        expand={expandSecurity}
        setExpand={setExpandSecurity}
        property="security"
      />           
      <Filter
        label="Business Models"
        expand={expandPricing}
        setExpand={setExpandPricing}
        property="pricing"
      />
    </Box>
  );

  return (
    <Data
      data={filteredReferences} // Use filtered references based on search query
      properties={properties}
      updateOn={showFiltersButton ? undefined : 'change'}
    >
      <Grid
        align="start"
        columns={pageContentGrid.columns[breakpoint]}
        gap={pageContentGrid.gap[breakpoint]}
      >
        {sidebar}
        <ListResults />
      </Grid>
    </Data>
  );
};